import { useIsMobile } from '@/hooks/useIsMobile';
import { Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { MTGCard } from './MTGCard';
import { Skeleton } from './ui/skeleton';
import { ScryfallCard } from '@/types/scryfall';
import { Button } from './ui/button';
import { useToast } from '@/hooks/useToast';
import { EDHRecCard as EDHRecCardType } from '@/hooks/useEDHRec';

interface EDHRecCardProps {
  card: EDHRecCardType;
  onCardAdded: (card: ScryfallCard) => void;
  onCardClick: (card: ScryfallCard) => void;
}

export function EDHRecCard({ card: edhrecCard, onCardAdded, onCardClick }: EDHRecCardProps) {
  const isMobile = useIsMobile();
  const { data: card, isLoading } = useQuery<ScryfallCard>({
    queryKey: ['scryfall', edhrecCard.oracle_id],
    queryFn: async () => {
      const response = await fetch(`https://api.scryfall.com/cards/search?q=oracle_id%3A${edhrecCard.oracle_id}`, {
        referrerPolicy: 'no-referrer',
      });
      const data = await response.json();
      return data.data[0];
    },
  });

  const { toast } = useToast();

  const addCardToDeck = (e: React.MouseEvent, card: ScryfallCard) => {
    e.stopPropagation();
    onCardAdded(card);
    toast({
      title: 'Card Added',
      description: `${card.name} has been added to your deck.`,
    });
  };

  if (isLoading) {
    return <Skeleton className="w-full h-64" />;
  }

  if (!card) {
    return null;
  }

  return (
    <div className="relative group cursor-pointer" onClick={() => onCardClick(card)}>
      <MTGCard card={card} />
      <div className={`absolute inset-0 flex items-center justify-center ${isMobile ? '' : 'bg-black/50 opacity-0 group-hover:opacity-100'} transition-opacity`}>
        <Button
          size="icon"
          variant="ghost"
          className="bg-black/50 hover:bg-black/75"
          onClick={(e) => addCardToDeck(e, card)}
        >
          <Plus className="h-6 w-6" />
        </Button>
      </div>
    </div>
  );
}
